/**
 * V70 엔글 마블 — 24칸 보드 생성 (결정론, epoch seed 기반)
 */
import type { EventKind } from '../config/eventConfig';
import { MARBLE_GLOBAL_CONFIG } from '../config/eventConfig';
import { createPrng, hashSeed } from './prng';
import type { BoardCell } from './types';

// non-safe 20칸 분포 (사용자 승인):
//   E1 2 / E2 2 / E3 4 / E4 2 / E5 2 / E6 2 / E7 2 / E8 2 / E9 1 / E10 1 = 20
//   safe 4 + 20 = 24
const NON_SAFE_POOL: EventKind[] = [
  'E1','E1','E2','E2','E3','E3','E3','E3',
  'E4','E4','E5','E5','E6','E6','E7','E7',
  'E8','E8','E9','E10',
];

function shuffle<T>(arr: T[], rand: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function generateBoard(epochSeed: number): BoardCell[] {
  const rand = createPrng(hashSeed(`marble-board-epoch-${epochSeed}`));
  const shuffled = shuffle([...NON_SAFE_POOL], rand);
  const cells: BoardCell[] = [];
  const safeSet = new Set<number>(MARBLE_GLOBAL_CONFIG.safePositions as readonly number[]);
  let idx = 0;
  for (let i = 0; i < MARBLE_GLOBAL_CONFIG.boardSize; i++) {
    cells.push({ index: i, kind: safeSet.has(i) ? 'safe' : shuffled[idx++] });
  }
  return cells;
}

/**
 * 24칸 사각 테두리 좌표 (7×7 grid 기준, 0-indexed).
 *
 *   row0: 0,1,2,3,4,5,6
 *   col6: 6,7,8,9,10,11,12
 *   row6: 12,13,14,15,16,17,18 (역순)
 *   col0: 18,19,20,21,22,23,0 (역순)
 *
 * 모서리: 0 (TL/START), 6 (TR), 12 (BR), 18 (BL). 중복/누락 없음.
 */
export function positionToGrid(pos: number): { row: number; col: number } {
  const p = ((pos % 24) + 24) % 24;
  if (p <= 6) return { row: 0, col: p };          // 상변: 0~6
  if (p <= 12) return { row: p - 6, col: 6 };     // 우변: 7~12
  if (p <= 18) return { row: 6, col: 18 - p };    // 하변: 13~18 (역순)
  return { row: 24 - p, col: 0 };                  // 좌변: 19~23
}

export const GRID_SIZE = 7;

export function cellEmoji(kind: EventKind): string {
  switch (kind) {
    case 'safe': return '✦';
    case 'E1': return '🎯';
    case 'E2': return '✊';
    case 'E3': return '🎁';
    case 'E4': return '🪙';
    case 'E5': return '💣';
    case 'E6': return '🃏';
    case 'E7': return '🔢';
    case 'E8': return '🛡';
    case 'E9': return '🌀';
    case 'E10': return '💎';
    case 'vault': return '💰';
    case 'shop': return '🏪';
  }
}

export function cellLabel(kind: EventKind): string {
  switch (kind) {
    case 'safe': return '안전';
    case 'E1': return '확정 vs 랜덤';
    case 'E2': return '가위바위보';
    case 'E3': return '보물상자';
    case 'E4': return '동전 던지기';
    case 'E5': return '폭탄 해체';
    case 'E6': return '운명의 카드';
    case 'E7': return '홀짝';
    case 'E8': return '방패 획득';
    case 'E9': return '워프';
    case 'E10': return '잭팟';
    case 'vault': return '보너스 금고';
    case 'shop': return '아이템 상점';
  }
}

/** V2 — safe index 별 역할 라벨 */
export function safeCornerLabel(index: number): string {
  if (index === 0) return 'START';
  if (index === 6) return '무인도';
  if (index === 12) return '보너스 금고';
  if (index === 18) return '아이템 상점';
  return '안전';
}
export function safeCornerEmoji(index: number): string {
  if (index === 0) return '🏁';
  if (index === 6) return '🏝';
  if (index === 12) return '💰';
  if (index === 18) return '🏪';
  return '✦';
}
